/**
 * useStrategyAIOrchestration Hook
 *
 * Centralized hook for AI orchestration setup in Strategy/Block forms.
 * Eliminates code duplication between StrategyFormDialog and BlockFormDialog.
 *
 * Features:
 * - Service instances (Audience, Task, StudyCase, Review, Rating, BigNumber, Project)
 * - AI services (SourceDataHydrator, AIPayloadBuilder, AIWebhookService, StrategyOrchestrator)
 * - Webhook settings loading from Firestore
 * - Source data loading via useSourceDataLoader
 * - Project data loading
 * - Current user info (for DataReferenceTreeWrapper)
 * - Memoization for optimal performance
 *
 * Related: Sprint 11, Refactoring task
 */

import { useState, useEffect, useMemo } from 'react';
import { AudienceService } from '@modules/firebase-core/services/AudienceService';
import { TaskService } from '@modules/firebase-core/services/TaskService';
import { StudyCaseService } from '@modules/firebase-core/services/StudyCaseService';
import { ReviewService } from '@modules/firebase-core/services/ReviewService';
import { RatingService } from '@modules/firebase-core/services/RatingService';
import { BigNumberService } from '@modules/firebase-core/services/BigNumberService';
import { WebhookSettingsService } from '@modules/firebase-core/services/WebhookSettingsService';
import { ProjectService } from '@modules/firebase-core/services/ProjectService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import { audienceConverter } from '@modules/firebase-core/converters/audienceConverter';
import { taskConverter } from '@modules/firebase-core/converters/taskConverter';
import { studyCaseConverter } from '@modules/firebase-core/converters/studyCaseConverter';
import { SourceDataHydrator } from '../services/SourceDataHydrator';
import { AIPayloadBuilder } from '../services/AIPayloadBuilder';
import { AIWebhookService } from '../services/AIWebhookService';
import { StrategyOrchestrator } from '../services/StrategyOrchestrator';
import type { WebhookSettings } from '@segmently/schema-models/settings';
import type { Project } from '@segmently/schema-models/entities';
import { useSourceDataLoader, type UseSourceDataLoaderResult } from './useSourceDataLoader';
import { auth } from '@frontend/firebase';

/**
 * Hook options
 */
export interface UseStrategyAIOrchestratorOptions {
  /** Project ID for data loading */
  projectId: string;

  /** Strategy service instance (must be created by caller) */
  strategyService: StrategyService;

  /** Auto-load data when true (typically when dialog opens) */
  open: boolean;
}

/**
 * Hook result
 */
export interface UseStrategyAIOrchestratorResult {
  // ================================
  // DATA SERVICES
  // ================================

  /** Audience service instance */
  audienceService: AudienceService;

  /** Task service instance */
  taskService: TaskService;

  /** Study case service instance */
  studyCaseService: StudyCaseService;

  /** Review service instance */
  reviewService: ReviewService;

  /** Rating service instance */
  ratingService: RatingService;

  /** Big number service instance */
  bigNumberService: BigNumberService;

  /** Webhook settings service instance */
  webhookSettingsService: WebhookSettingsService;

  /** Project service instance */
  projectService: ProjectService;

  // ================================
  // AI SERVICES
  // ================================

  /** Source data hydrator (loads entities from Firestore) */
  sourceDataHydrator: SourceDataHydrator;

  /** AI payload builder (prepares data for webhook) */
  aiPayloadBuilder: AIPayloadBuilder;

  /** AI webhook service (calls external AI API) */
  aiWebhookService: AIWebhookService;

  /** Strategy orchestrator (high-level AI operations) */
  strategyOrchestrator: StrategyOrchestrator;

  // ================================
  // WEBHOOK SETTINGS
  // ================================

  /** Loaded webhook settings from Firestore */
  webhookSettings: WebhookSettings | null;

  /** Loading state for webhook settings */
  webhookSettingsLoading: boolean;

  // ================================
  // SOURCE DATA (from useSourceDataLoader)
  // ================================

  /** Loaded source data */
  sourceData: UseSourceDataLoaderResult['data'];

  /** Loading state for source data */
  sourceDataLoading: boolean;

  /** Error if source data loading failed */
  sourceDataError: Error | null;

  // ================================
  // PROJECT DATA
  // ================================

  /** Loaded project data */
  project: Project | null;

  /** Loading state for project data */
  projectLoading: boolean;

  // ================================
  // AUTH
  // ================================

  /** Current user (for DataReferenceTreeWrapper) */
  currentUser: { uid: string } | undefined;
}

/**
 * useStrategyAIOrchestration Hook
 *
 * Provides all services and data needed for Strategy/Block AI forms.
 * Replaces 50+ lines of duplicated code in each dialog component.
 *
 * Usage:
 * ```typescript
 * const orchestration = useStrategyAIOrchestration({
 *   projectId,
 *   strategyService,
 *   open: dialogOpen
 * });
 *
 * // Access services
 * orchestration.strategyOrchestrator.createWithAIServer(...);
 *
 * // Access data
 * <DataReferenceTreeWrapper
 *   audiences={orchestration.sourceData.audiences}
 *   tasks={orchestration.sourceData.tasks}
 *   {...}
 * />
 *
 * // Check webhook configuration
 * if (orchestration.webhookSettings?.aiStrategy) {
 *   // AI enabled
 * }
 * ```
 */
export function useStrategyAIOrchestration(
  options: UseStrategyAIOrchestratorOptions
): UseStrategyAIOrchestratorResult {
  const { projectId, strategyService, open } = options;

  // ================================
  // STATE
  // ================================

  const [webhookSettings, setWebhookSettings] = useState<WebhookSettings | null>(null);
  const [webhookSettingsLoading, setWebhookSettingsLoading] = useState(false);
  const [project, setProject] = useState<Project | null>(null);
  const [projectLoading, setProjectLoading] = useState(false);

  // ================================
  // DATA SERVICES (memoized)
  // ================================

  const webhookSettingsService = useMemo(() => new WebhookSettingsService(), []);
  const projectService = useMemo(() => new ProjectService(), []);
  const audienceService = useMemo(
    () => new AudienceService(projectId, audienceConverter),
    [projectId]
  );
  const taskService = useMemo(
    () => new TaskService(projectId, taskConverter),
    [projectId]
  );
  const studyCaseService = useMemo(
    () => new StudyCaseService(projectId, studyCaseConverter),
    [projectId]
  );
  const reviewService = useMemo(() => new ReviewService(projectId), [projectId]);
  const ratingService = useMemo(() => new RatingService(projectId), [projectId]);
  const bigNumberService = useMemo(() => new BigNumberService(projectId), [projectId]);

  // ================================
  // AI SERVICES (memoized)
  // ================================

  const sourceDataHydrator = useMemo(
    () =>
      new SourceDataHydrator(
        projectId,
        audienceService,
        taskService,
        studyCaseService,
        reviewService,
        ratingService,
        bigNumberService
      ),
    [projectId, audienceService, taskService, studyCaseService, reviewService, ratingService, bigNumberService]
  );

  const aiPayloadBuilder = useMemo(() => new AIPayloadBuilder(), []);
  const aiWebhookService = useMemo(() => new AIWebhookService(), []);

  const strategyOrchestrator = useMemo(
    () =>
      new StrategyOrchestrator(
        projectId,
        strategyService,
        sourceDataHydrator
      ),
    [projectId, strategyService, sourceDataHydrator]
  );

  // ================================
  // SOURCE DATA LOADING
  // ================================

  const {
    data: sourceData,
    loading: sourceDataLoading,
    error: sourceDataError,
  } = useSourceDataLoader({
    projectId,
    autoLoad: open,
    limit: 50,
  });

  // ================================
  // WEBHOOK SETTINGS LOADING
  // ================================

  useEffect(() => {
    if (!open) {
      return;
    }

    setWebhookSettingsLoading(true);

    webhookSettingsService
      .getWebhookUrls()
      .then(settings => {
        setWebhookSettings(settings);
        console.log(
          '[useStrategyAIOrchestration] Webhook settings loaded:',
          settings?.aiStrategy ? 'configured' : 'NOT CONFIGURED'
        );
      })
      .catch(error => {
        console.error('[useStrategyAIOrchestration] Failed to load webhook settings:', error);
        setWebhookSettings(null);
      })
      .finally(() => {
        setWebhookSettingsLoading(false);
      });
  }, [open, webhookSettingsService]);

  // ================================
  // PROJECT LOADING
  // ================================

  useEffect(() => {
    if (!open || !projectId) {
      return;
    }

    setProjectLoading(true);

    projectService
      .get(projectId)
      .then(projectData => {
        setProject(projectData);
        console.log('[useStrategyAIOrchestration] Project loaded:', projectData?.name);
      })
      .catch(error => {
        console.error('[useStrategyAIOrchestration] Failed to load project:', error);
        setProject(null);
      })
      .finally(() => {
        setProjectLoading(false);
      });
  }, [open, projectId, projectService]);

  // ================================
  // MEMOIZED VALUES
  // ================================

  // Memoize currentUser to prevent unnecessary re-renders
  // Only recreate object when uid actually changes
  const currentUser = useMemo(() => {
    return auth.currentUser ? { uid: auth.currentUser.uid } : undefined;
  }, [auth.currentUser?.uid]);

  // ================================
  // RETURN RESULT
  // ================================

  return {
    // Data services
    audienceService,
    taskService,
    studyCaseService,
    reviewService,
    ratingService,
    bigNumberService,
    webhookSettingsService,
    projectService,

    // AI services
    sourceDataHydrator,
    aiPayloadBuilder,
    aiWebhookService,
    strategyOrchestrator,

    // Webhook settings
    webhookSettings,
    webhookSettingsLoading,

    // Source data
    sourceData,
    sourceDataLoading,
    sourceDataError,

    // Project data
    project,
    projectLoading,

    // Auth
    currentUser,
  };
}
